import { css } from '@emotion/react';
import { ColDef, CsvExportParams, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { AgTable } from '../../../../components/AgTable/AgTable';
import { InvestorCashFlowRow, investorCashFlowRowSchema } from '../../../../schemas/InvestorCashFlows.schema';
import { withOverrides } from '../../../../util/ag-grid-utils';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { defaultColDef } from '../../../Finance2/common-grid-defs/defaultColDefs';
import { SummaryPanelBodyHeight } from '../summaryPanelBodyHeight';
import { useInvestorCashFlowsGridFilter } from './useInvestorCashFlowsGridFilter';

// Same height as the chart's plot box below; a longer ledger scrolls inside the grid and the header stays put.
const gridContainerCss = css`
  height: ${SummaryPanelBodyHeight};
`;

// Visible columns first; the hidden reconciliation columns follow for the column menu and export.
const ColumnOrder: (keyof InvestorCashFlowRow)[] = [
  'fundName',
  'investorType',
  'transactionDate',
  'amount',
  'nature',
  'distributionType',
  'subType',
  'signedAmount',
  'ledgerTransType',
  'ledgerAccount',
  'ledgerComment',
  'transactionEntryId',
  'id',
];

// Newest first, with the id as a stable tie-break within a day.
const columnOverrides: Record<string, ColDef> = {
  transactionDate: { initialSort: 'desc', initialSortIndex: 0 },
  id: { initialSort: 'asc', initialSortIndex: 1 },
};

// Narrower than the shared 200 so the seven visible columns fit without an immediate horizontal scroll.
const gridDefaultColDef: ColDef = { ...defaultColDef, minWidth: 150 };

interface IInvestorCashFlowsGridProps {
  rows: InvestorCashFlowRow[];
  exportParams: CsvExportParams;
  /** Captured by the owner in `onGridReady` and dropped in `onGridPreDestroyed`; the type filter re-runs against it. */
  gridApi: GridApi<InvestorCashFlowRow> | null;
  onGridReady: (event: GridReadyEvent<InvestorCashFlowRow>) => void;
  onGridPreDestroyed: () => void;
}

export function InvestorCashFlowsGrid({
  rows,
  exportParams,
  gridApi,
  onGridReady,
  onGridPreDestroyed,
}: IInvestorCashFlowsGridProps) {
  const schemaToGrid = useSchemaToGrid();
  const { doesExternalFilterPass, isExternalFilterPresent } = useInvestorCashFlowsGridFilter(gridApi);

  const columnDefs = useMemo(
    () => withOverrides(schemaToGrid(investorCashFlowRowSchema(), ColumnOrder), columnOverrides),
    [schemaToGrid]
  );

  const getRowId = useCallback((params: GetRowIdParams<InvestorCashFlowRow>) => String(params.data.id), []);

  return (
    <div css={gridContainerCss}>
      <AgTable
        columnDefs={columnDefs}
        defaultColDef={gridDefaultColDef}
        defaultCsvExportParams={exportParams}
        doesExternalFilterPass={doesExternalFilterPass}
        getRowId={getRowId}
        isExternalFilterPresent={isExternalFilterPresent}
        onGridPreDestroyed={onGridPreDestroyed}
        onGridReady={onGridReady}
        rowData={rows}
        rowGroupPanelShow='never'
      />
    </div>
  );
}
